<template>
    <div class="rounded-md shadow-md h-full" :style="'height:' + maxHeight + 'px;' + 'width:' + maxWidth + 'px'">
        <!-- Youtube -->
        <div v-if="data.type === 'youtube'" class="text-blue-dark h-full w-full">
            <YoutubePlayer v-if="active" :source="data.meta.snippet.resourceId.videoId" />
            <div v-else class="h-full w-full rounded-md relative" @click="active = true">
                <img alt="Youtube Cover" :src="data?.meta?.snippet?.thumbnails?.high?.url" class="w-full h-full object-cover rounded-md" />
                <div class="z-10 w-full h-full preview absolute top-0 bottom-0 left-0 right-0 rounded-md">
                    <div class="w-full h-full flex flex-col justify-center items-center bg-cover bg-no-repeat bg-center">
                        <img alt="Play" style="width: 50px; height: 50px" :src="$asset('/hkcm/images/play.png', 'public')" />
                    </div>
                </div>
            </div>
        </div>
        <!-- Twitter -->
        <a
            v-if="data.type === 'twitter'"
            :href="xLink + `/status/${data?.meta?.id}`"
            target="_blank"
            class="text-blue-dark group p-4 rounded-md relative overflow-hidden h-full bg-white hover:bg-blue-light transition-all ease-in-out duration-300 block min-h-[250px]"
        >
            <FontAwesomeIcon class="w-auto text-lg absolute top-2 right-2 text-[#1DA1F2]" :icon="['fab', 'twitter']" />
            <div v-if="data.meta.attachments.find((attachment) => attachment.type === 'photo')">
                <div
                    :style="`background-image: url(${data.meta.attachments.find((attachment) => attachment.type === 'photo').url})`"
                    class="absolute top-0 left-0 w-full h-full group-hover:scale-110 transition-all ease-in-out duration-500 bg-cover bg-no-repeat bg-center"
                ></div>
                <div class="overlay-bottom-dark absolute top-0 bottom-0 left-0 right-0 w-full h-full z-[5]" />

                <div class="absolute top-0 left-0 w-full h-full p-4 flex items-end text-white z-[10] group-hover:backdrop-blur-sm group-hover:bg-blue-dark/30">
                    <div class="w-full">
                        <small class="block">
                            {{ formatDateByRegion($moment(data.meta.created_at)) }}
                        </small>

                        <div v-html="data.meta.text" class="max-h-0 group-hover:max-h-[400px] line-clamp-3 transition-all ease-in-out duration-500 overflow-hidden delay-500"></div>

                        <div class="max-h-0 group-hover:max-h-[200px] transition-all ease-in-out duration-500 overflow-hidden delay-500">
                            <div class="flex flex-row justify-between my-2 gap-4">
                                <div class="flex flex-row border-t border-gray-300 my-4 pt-4 gap-4" v-if="data?.meta?.entities?.hashtags?.length">
                                    <div
                                        v-for="hashtag in data?.meta?.entities?.hashtags.slice(0, 5)"
                                        :key="hashtag.tag"
                                        class="rounded-full bg-blue-light p-2 flex flex-row gap-2 items-center"
                                    >
                                        <div class="w-4 h-4 rounded-full bg-blue" />
                                        <span class="text-xs text-blue-dark">
                                            {{ hashtag.tag }}
                                        </span>
                                    </div>
                                </div>
                                <div class="flex flex-row gap-2 group-hover:gap-3 transition-all ease-in-out duration-500 items-center mt-2">
                                    <span target="_blank">
                                        {{ $t('read_more') }}
                                    </span>
                                    <Icon type="ArrowRight" viewBox="0 0 29 21" width="18" height="10" class="fill-none stroke-current" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else class="flex flex-col justify-between h-full">
                <div>
                    <small class="block">
                        {{ formatDateByRegion($moment(data.meta.created_at)) }}
                    </small>
                    <div v-html="data.meta.text" class="font-thin mt-2 line-clamp-5 self-start"></div>
                </div>
                <div class="flex flex-row gap-2 group-hover:gap-3 transition-all ease-in-out duration-500 items-center mt-2">
                    <div v-if="data?.meta?.entities?.urls">
                        <a target="_blank" :href="data?.meta?.entities?.urls[0]?.expanded_url">
                            {{ $t('read_more') }}
                        </a>
                    </div>
                    <p v-else>&nbsp;</p>
                    <Icon type="ArrowRight" viewBox="0 0 29 21" width="18" height="10" class="fill-none stroke-current" />
                </div>
                <div class="flex flex-row border-t border-gray-300 my-4 pt-4 gap-4 w-full overflow-x-scroll" v-if="data?.meta?.entities?.hashtags?.length">
                    <div v-for="hashtag in data?.meta?.entities?.hashtags?.slice(0, 5)" :key="hashtag.tag" class="rounded-full bg-blue-light p-2 flex flex-row gap-2 items-center">
                        <div class="w-4 h-4 rounded-full bg-blue" />
                        <span class="text-xs text-blue-dark">
                            {{ hashtag.tag }}
                        </span>
                    </div>
                </div>
            </div>
        </a>

        <!-- Tradingroom -->
        <router-link
            v-if="data.type === 'tradingroom'"
            :to="`/hkcmnews/${data.slug.name}`"
            class="text-blue-dark group p-4 rounded-md relative overflow-hidden h-full bg-white hover:bg-blue-light transition-all ease-in-out duration-300 block"
        >
            <img class="w-auto h-[20px] absolute top-2 right-2" :src="$asset('/hkcm/images/logo-small-on-light-background.svg', 'public')" alt="HKCM Logo" title="HKCM Logo" />
            <div class="flex flex-col justify-between h-full">
                <div>
                    <small class="block">
                        {{ formatDateByRegion($moment(data.published_at)) }}
                    </small>
                    <h4 class="base_bold block self-start line-clamp-1">
                        {{ $translate(data.title) }}
                    </h4>
                    <div v-html="$translate(data.teaser)" class="font-thin line-clamp-1 self-start"></div>
                </div>
            </div>
        </router-link>
    </div>
</template>

<script>
import { formatDateByRegion } from '../../helpers.js';

export default {
    methods: { formatDateByRegion },
    props: ['data', 'maxWidth', 'maxHeight'],
    data: () => ({
        active: false
    }),
    computed: {
        region() {
            return window.meta?.region_configuration?.region || 'de';
        },
        xLink() {
            return this.region === 'de' ? 'https://twitter.com/HKCM1' : 'https://twitter.com/hkcm_global';
        }
    }
};
</script>
